import React, { useState, useEffect } from "react";
import { useCart } from "./CartContext";
import { Link, useNavigate } from "react-router-dom";
import { 
  Plus, 
  Minus, 
  Trash2, 
  ShoppingCart, 
  Store, 
  ArrowRight, 
  ShieldCheck, 
  Gift 
} from "lucide-react"; 
import usePlatformFees from "./hooks/usePlatformFees";
import api from "./axiosConfig";

export default function Panier() {
  const {
    cart,
    merchantName,
    increaseQty,
    decreaseQty,
    removeFromCart,
    total,
    promoActive, // ✅ ON RECUPERE LA VALEUR DU CONTEXT ICI
  } = useCart();

  const { serviceFee, deliveryFee, loading } = usePlatformFees();
  
  // Suppression du useState local promoActive qui bloquait l'affichage
  const [loadingPromo, setLoadingPromo] = useState(false);
  const navigate = useNavigate();

  // On peut garder ce useEffect pour forcer un check visuel si tu veux, 
  // mais c'est le promoActive du destructuring au-dessus qui pilotera l'affichage.

  const passerCommande = () => {
    if (cart.length === 0) return;
    navigate("/pre-commande");
  };

  // ✅ CALCUL DYNAMIQUE ET SECURISATION DES TYPES
  // On force le passage en Number pour éviter les concaténations si les frais admin sont des strings
  const safeTotal = Number(total) || 0;
  const safeServiceFee = Number(serviceFee) || 0;
  const safeDeliveryFee = Number(deliveryFee) || 0;

  const currentServiceFee = promoActive ? 0 : safeServiceFee;
  const finalTotal = safeTotal + currentServiceFee + safeDeliveryFee;

  const getImageUrl = (img) => {
    if (!img) return null;
    if (img.startsWith("http")) return img;
    return `https://souqi-api.onrender.com${img.startsWith("/") ? "" : "/"}${img}`;
  };

  return (
    <div className="min-h-screen px-4 pb-20 font-sans pt-32 bg-[#FAFAFA]">
      <div className="max-w-6xl mx-auto">
        
        <div className="flex flex-col gap-2 mb-10 animate-fade-up">
            <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 md:text-5xl">
            Mon Panier
            </h1>
            {merchantName && (
            <div className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-100 rounded-full shadow-sm w-fit">
                <Store size={16} className="text-[#f34141]" />
                <span className="text-sm font-bold text-slate-600">
                  Votre sélection chez <span className="text-slate-900">{merchantName}</span>
                </span>
            </div>
            )}
        </div>

        {cart.length === 0 ? (
          <div className="max-w-md p-12 mx-auto mt-20 text-center bg-white shadow-sm border border-gray-100 rounded-[3rem] animate-fade-up">
            <div className="flex items-center justify-center w-20 h-20 mx-auto mb-6 rounded-full bg-gray-50">
                <ShoppingCart size={40} className="text-gray-300" />
            </div>
            <p className="mb-8 text-xl font-medium text-gray-500">
              Votre panier est encore vide.
            </p>
            <Link
              to="/"
              className="inline-flex items-center justify-center w-full px-8 py-4 text-white bg-gradient-to-r from-[#f34141] to-[#ff6b6b] rounded-2xl font-bold shadow-lg shadow-red-500/20 hover:shadow-red-500/40 transition-all duration-300 active:scale-95"
            >
              Découvrir les boutiques
            </Link>
          </div>
        ) : (
          <div className="grid gap-10 lg:grid-cols-3">
            
            <div className="space-y-4 lg:col-span-2 animate-fade-up" style={{ animationDelay: '0.1s' }}>
              {cart.map((item) => (
                <div
                  key={item.id}
                  className="group flex items-center p-4 bg-white border border-gray-50 shadow-sm rounded-[2rem] transition-all duration-300 hover:shadow-md hover:border-red-50"
                >
                  <div className="flex-shrink-0 w-24 h-24 overflow-hidden bg-gray-50 rounded-2xl">
                    {getImageUrl(item.image) && (
                      <img
                        src={getImageUrl(item.image)}
                        className="object-contain w-full h-full p-2 transition-transform duration-500 group-hover:scale-110"
                        alt={item.name}
                      />
                    )}
                  </div>

                  <div className="flex-1 ml-6">
                    <div className="flex items-start justify-between">
                        <div>
                            <h2 className="mb-1 text-lg font-bold leading-tight text-slate-900">{item.name}</h2>
                            <p className="text-xl font-black text-[#f34141]">
                            {item.price} <span className="text-xs">MRU</span>
                            </p>
                        </div>
                        <button
                            onClick={() => removeFromCart(item.id)}
                            className="p-2 text-gray-300 transition-colors hover:text-red-500"
                            title="Supprimer"
                        >
                            <Trash2 size={20} />
                        </button>
                    </div>

                    <div className="flex items-center gap-4 mt-4">
                      <div className="flex items-center p-1 border border-gray-100 bg-gray-50 rounded-xl">
                        <button
                            onClick={() => decreaseQty(item.id)}
                            disabled={item.quantity <= 1}
                            className={`p-1.5 rounded-lg transition-all ${item.quantity <= 1 ? 'text-gray-200' : 'text-slate-600 hover:bg-white hover:shadow-sm'}`}
                        >
                            <Minus size={16} strokeWidth={3} />
                        </button>
                        <span className="w-10 font-bold text-center text-slate-800">
                            {item.quantity}
                        </span>
                        <button
                            onClick={() => increaseQty(item.id)}
                            className="p-1.5 text-slate-600 rounded-lg transition-all hover:bg-white hover:shadow-sm"
                        >
                            <Plus size={16} strokeWidth={3} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="animate-fade-up" style={{ animationDelay: '0.2s' }}>
              <div className="sticky p-8 bg-white border border-gray-100 shadow-xl rounded-[2.5rem] top-32">
                <h2 className="mb-6 text-2xl font-black text-slate-900">Résumé</h2>

                <div className="pb-6 space-y-4 border-b border-gray-200 border-dashed">
                  <div className="flex justify-between font-medium text-slate-500">
                    <span>Sous-total</span>
                    <span className="font-bold text-slate-900">{total} MRU</span>
                  </div>

                  {/* REMPLACER LA SECTION DES FRAIS PAR CE BLOC */}
                  {loading ? (
                    <div className="flex items-center gap-2 py-2">
                        <div className="w-4 h-4 border-2 border-red-500 rounded-full border-t-transparent animate-spin"></div>
                        <span className="text-xs font-bold tracking-widest text-gray-400 uppercase">Calcul des frais...</span>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3">
                      <div className="flex justify-between font-medium text-slate-500">
                        <span>Frais de service Souqi</span>
                        {promoActive ? (
                          <div className="flex flex-col items-end">
                            <span className="text-xs font-bold text-gray-400 line-through">{serviceFee} MRU</span>
                            <span className="font-black text-[#f34141] flex items-center gap-1 animate-pulse">
                               OFFERT <Gift size={12} />
                            </span>
                          </div>
                        ) : (
                          <span className="font-bold text-slate-900">{serviceFee} MRU</span>
                        )}
                      </div>
                  
                      {/* ✅ MENTION LIVRAISON CLAIRE ET VISIBLE */}
                      <div className="flex items-start gap-2 p-3 bg-orange-50 border border-orange-100 rounded-xl">
                        <div className="mt-0.5 text-orange-500">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg>
                        </div>
                        <p className="text-[10px] leading-tight font-black uppercase tracking-tight text-orange-700">
                          La livraison est à régler directement au livreur lors de la réception.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-end justify-between mt-6 mb-8">
                  <span className="font-bold text-slate-500">Total à payer</span>
                  <div className="text-right">
                    <span className="block text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#f34141] to-[#ff6b6b]">
                        {finalTotal} MRU
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 p-3 mb-6 text-blue-700 border border-blue-100 bg-blue-50 rounded-2xl">
                    <ShieldCheck size={20} />
                    <span className="text-xs font-bold tracking-tight uppercase">Paiement sécurisé</span>
                </div>

                <button
                  onClick={passerCommande}
                  className="group relative flex items-center justify-center w-full py-5 font-black text-white bg-gradient-to-r from-[#f34141] to-[#ff6b6b] rounded-2xl shadow-lg shadow-red-500/25 hover:shadow-red-500/40 transition-all duration-300 active:scale-[0.98]"
                >
                  <span className="mr-2">Passer la commande</span>
                  <ArrowRight size={20} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                {/* La mention de retrait a été supprimée ici */}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
